import { Button, Form, Input, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cryptoRandomString from 'crypto-random-string';
import { useNavigate } from 'react-router-dom';
import { TStore } from '@/common/basis/types/tstore';
import { encrypt } from '@/common/utils/crypto-js';
import { TCryptoJS } from '@/common/basis/types/tcrypto-js';
import { userLogin } from '@/common/http/user';
import { isLicenseExpire, isSuccessCode } from '@/common/utils/response';
import { logger } from '@/common/utils/logger';
import { TField } from '@/common/basis/types/tfield';
import { getLicenceSign } from '@/common/http/logger';
import { getLocalUserName, setLocalUserName } from '@/common/utils/local-storage';
import { TRouter } from '@/common/basis/types/trouter';
import { setUser } from '@/common/store/person/slice';
import { setCookieUserToken } from '@/common/utils/cookies';

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm<Partial<{ loginName: string; pwd: string }>>();
  const [formData, setFormData] = useState<Partial<{ loginName: string; pwd: string }>>();
  const isOnline = useSelector((state: TStore.State) => state.network.isOnline);
  const [messageApi, contextHolder] = message.useMessage({ top: 60 });
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const user = useSelector((state: TStore.State) => state.person.user);

  // 获取license，记录地址信息
  const loggerLicenceSign = async () => {
    const resp = await getLicenceSign();

    if (isSuccessCode(resp?.code)) {
      logger().info('LicenceSign:', resp?.data);
    }
  };

  // 登录
  const handLogin = async () => {
    if (isLoginLoading) {
      return;
    }

    try {
      const values = await form.validateFields();
      const loginName = values?.loginName ?? '';
      const nonce = cryptoRandomString({ length: 16 });
      const password = encrypt({ str: values.pwd ?? '', key: nonce, iv: TCryptoJS.USER_SERVER_IV });

      if (!isOnline) {
        messageApi.destroy();
        messageApi.warning(t('networkError'));

        return;
      }
      setIsLoginLoading(true);
      const resp = await userLogin({ loginName, nonce, password });

      if (isSuccessCode(resp?.code)) {
        const token = resp.data?.token ?? '';

        loggerLicenceSign(); // 登录成功后, 记录license
        setCookieUserToken(token); // 存储登录用的token
        setLocalUserName(loginName); // 存储登录用的用户名
        dispatch(setUser({ ...user, token }));
        navigate(TRouter.ROUTE_HOME, { replace: true });
      } else if (isLicenseExpire(resp?.code)) {
        const roleCode = resp?.data?.roleCode;
        const isLicenceTrail = resp?.data?.licenceTrail ?? true;
        const isAdminUser = roleCode === TField.USER_POWSER_ADMIN;

        if (isLicenceTrail) {
          if (isAdminUser) {
            messageApi.destroy();
            messageApi.warning(t('trialExpiredAdmin')); // 免费试用已到期(管理员登录提示)
          } else {
            messageApi.destroy();
            messageApi.warning(t('trialExpired')); // 免费试用已到期(非管理员登录提示)
          }
        } else {
          if (isAdminUser) {
            messageApi.destroy();
            messageApi.warning(t('licenseExpiredAdmin')); // 正式版已到期(管理员登录提示)
          } else {
            messageApi.destroy();
            messageApi.warning(t('licenseExpired')); // 正式版已到期(非管理员登录提示)
          }
        }
      } else {
        messageApi.destroy();
        messageApi.warning(resp?.msg);
      }
    } catch (err: any) {
      if (err.message) {
        messageApi.destroy();
        messageApi.warning(t('loginFailed'));
      }
    }
    setIsLoginLoading(false);
  };

  useEffect(() => {
    const loginName = getLocalUserName();

    if (loginName) {
      form.setFieldValue('loginName', loginName);
      setFormData({ ...formData, loginName });
    }
  }, []);

  return (
    <>
      <div
        className='flex h-screen w-screen flex-col items-center bg-cover bg-fixed bg-center bg-no-repeat'
        style={{ backgroundImage: `url(${require('@/common/basis/assets/bg.png')})` }}
      >
        <div className='relative m-auto h-[400px] w-[448px] overflow-hidden rounded-[15px] bg-white'>
          <div className='mx-[18px] flex flex-1 flex-col'>
            <div className='mt-8 overflow-hidden'>
              <img
                className='block h-[72px] w-[72px] object-cover'
                src={require('@/common/basis/assets/app_logo_icon.png')}
              />
            </div>
            <div className='my-[22px] text-[27px] font-medium text-[#131415]'>{t('loginProduct')}</div>
            <div className='mb-8'>
              <Form form={form} onValuesChange={(values) => setFormData({ ...formData, ...values })}>
                <Form.Item name='loginName' rules={[{ required: true, message: t('idNotEmpty') }]}>
                  <Input allowClear placeholder={t('enterId')} style={{ height: '43px', fontSize: '14px' }} />
                </Form.Item>
                <Form.Item name='pwd' rules={[{ required: true, message: t('passwordNotEmpty') }]}>
                  <Input.Password placeholder={t('enterPassword')} style={{ height: '43px', fontSize: '14px' }} />
                </Form.Item>
                <Form.Item className='mt-[17px]'>
                  <Button
                    block
                    disabled={!(formData?.loginName && formData.pwd)}
                    htmlType='submit'
                    loading={isLoginLoading}
                    onClick={() => handLogin()}
                    style={{ height: '55px', fontSize: '18px' }}
                    type='primary'
                  >
                    <span>{t('login')}</span>
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {contextHolder}
    </>
  );
}

export default App;
